import { nextTick } from 'vue';

import { useGlobalStore } from '@/stores/globalStore';
import { trackAction } from '@/composables/tracking/useTracking';

/** Message `type` values (contract: `banxa:*` event names). */
export const IFRAME_EVENT = {
  LOADING: 'banxa:loading',
  READY: 'banxa:ready',
  PAYMENT_PENDING: 'banxa:payment-pending',
  PAYMENT_COMPLETE: 'banxa:payment-complete',
  ERROR: 'banxa:error',
  KYC_COMPLETE: 'banxa:kyc-complete',
};

/**
 * postMessage bridge for partner pages embedding this app in an iframe when
 * `embeddedButtonOnlyIframeEnabled` is true (see globalStore.getEmbeddedButtonOnlyIframeMode).
 *
 * Payload shapes (see product contract):
* - banxa:loading — { type, status: "loading" }
* - banxa:ready — { type, width, height }
 * - banxa:payment-pending — { type, status: "pending" }
 * - banxa:payment-complete — { type, status: "complete" }
 * - banxa:error — { type, code, message }
 *
 * @param {(payload: object) => void} [onNotify] - Optional hook invoked with
 * every payload actually posted to the parent window. This lets other
 * transports (e.g. the native WebView bridge) mirror these events without
 * this composable knowing anything about them — see `usePartnerNotifications`,
 * which is the orchestrator that wires that up. Direct callers that only need
 * the iframe bridge can omit it.
 */
export default function usePartnerIframePostMessage(onNotify) {
  const globalStore = useGlobalStore();

  const getTargetOrigin = () => {
    const domain = globalStore?.getEmbeddedAPMerchantDomain;
    if (!domain) {
      trackAction('EMBEDDED_MERCHANT_DOMAIN_NOT_SET', {
        error: 'Embedded merchant domain is not set in the merchant settings',
      });
      return '*';
    }
    return domain.match(/^https?:\/\//) ? domain : `https://${domain}`;
  };

  const post = (payload) => {
    if (!globalStore.getEmbeddedButtonOnlyIframeMode) return;
    window.parent.postMessage(payload, getTargetOrigin());
    onNotify?.(payload);
  };

  const measureContentSize = (nativeButtonId) => {
    const space = document.getElementById(nativeButtonId);
    if (!space) return { width: 0, height: 0 };

    const spaceRect = space.getBoundingClientRect();

    return {
      width: spaceRect.width,
      height: spaceRect.height,
    };
  };

  const notifyLoading = () => {
    post({
      type: IFRAME_EVENT.LOADING,
      status: 'loading',
    });
  };

  const notifyReady = (nativeButtonId) => {
    nextTick(() => {
      requestAnimationFrame(() => {
        const { width, height } = measureContentSize(nativeButtonId);
        if (width <= 0 || height <= 0) {
          trackAction('EMBEDDED_BUTTON_NOT_READY', {
            nativeButtonId,
            width,
            height,
          });
        }
        post({
          type: IFRAME_EVENT.READY,
          width,
          height,
        });
      });
    });
  };

  const notifyPaymentPending = () => {
    post({
      type: IFRAME_EVENT.PAYMENT_PENDING,
      status: 'pending',
    });
  };

  const notifyPaymentComplete = () => {
    post({
      type: IFRAME_EVENT.PAYMENT_COMPLETE,
      status: 'complete',
    });
  };

  /**
   * Embedded partners get this instead of a return-URL redirect, which they cannot act on from
   * inside an iframe. post() is a no-op unless the merchant is embedded.
   */
  const notifyKycComplete = () => {
    post({
      type: IFRAME_EVENT.KYC_COMPLETE,
      status: 'complete',
    });
  };

  /**
   * @param {string|number} code
   * @param {string} [message]
   */
  const notifyError = (code, message = '') => {
    post({
      type: IFRAME_EVENT.ERROR,
      code,
      message: message ?? '',
    });
  };

  return {
    notifyLoading,
    notifyReady,
    notifyPaymentPending,
    notifyPaymentComplete,
    notifyKycComplete,
    notifyError,
  };
}
